import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DocumentUploadForm from "@/components/DocumentUploadForm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Upload, Download, Trash2, Loader2, Plus, ShieldAlert, AlertTriangle,
  Briefcase, GraduationCap, HardHat, HeartPulse, Inbox, Users, CheckCircle2, Lock, Sparkles,
} from "lucide-react";

const SECTIONS = [
  { key: "rrhh", label: "RRHH", icon: Briefcase },
  { key: "formacion", label: "Formación", icon: GraduationCap },
  { key: "prl_epis", label: "PRL y EPIs", icon: HardHat },
  { key: "salud", label: "Salud", icon: HeartPulse },
];

const downloadDoc = async (id, name) => {
  try {
    const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.download = name || "documento"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar");
  }
};

function DocTable({ docs, onDelete, showEmployee = true }) {
  if (!docs.length) return <div className="text-center text-slate-400 py-10 text-sm">No hay documentos en esta sección.</div>;
  return (
    <div className="space-y-2">
      {docs.map((d) => (
        <Card key={d.id} className="p-4 border-slate-200 shadow-none flex items-center justify-between gap-3" data-testid={`doc-${d.id}`}>
          <div className="min-w-0">
            <div className="font-medium text-slate-800 flex items-center gap-2 flex-wrap">
              {d.title}
              {d.requires_ack && (d.acknowledged
                ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] gap-1"><CheckCircle2 className="h-3 w-3" /> Recibido</Badge>
                : <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Pendiente acuse</Badge>)}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              {showEmployee && <span>{d.employee_name} · </span>}
              {d.issue_date || "—"}
              {d.expiry_date && <span className="text-amber-600"> · caduca {d.expiry_date}</span>}
              {d.item && <span> · {d.item}{d.item_size ? ` (${d.item_size})` : ""}</span>}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            {d.file_path && d.can_download && <Button size="icon" variant="ghost" onClick={() => downloadDoc(d.id, d.file_name)} data-testid={`download-${d.id}`}><Download className="h-4 w-4" /></Button>}
            {onDelete && <Button size="icon" variant="ghost" className="hover:text-red-500" onClick={() => onDelete(d.id)} data-testid={`del-doc-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const isAdmin = user?.role === "company_admin";
  const perms = user?.permissions || {};
  const [features, setFeatures] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [empFilter, setEmpFilter] = useState("");
  const [tab, setTab] = useState("rrhh");
  const [docs, setDocs] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => { api.get("/company/features").then(({ data }) => setFeatures(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/doc-employees").then(({ data }) => setEmployees(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/documents/expiring").then(({ data }) => setExpiring(data)).catch(() => {}); }, []);

  const canRead = (sec) => isAdmin || perms[sec]?.read || (sec === "salud" && perms[sec]?.write);
  const canWrite = (sec) => isAdmin || perms[sec]?.write;
  const visibleSections = SECTIONS.filter((s) => features?.sections?.[s.key] && canRead(s.key));

  const loadDocs = useCallback(async () => {
    if (!SECTIONS.find((s) => s.key === tab)) { setDocs([]); return; }
    setLoading(true);
    try {
      const qs = empFilter ? `&user_id=${empFilter}` : "";
      const { data } = await api.get(`/documents?section=${tab}${qs}`);
      setDocs(data);
    } catch { setDocs([]); }
    finally { setLoading(false); }
  }, [tab, empFilter]);
  useEffect(() => { if (features) loadDocs(); }, [features, loadDocs]);

  const delDoc = async (id) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    await api.delete(`/documents/${id}`); toast.success("Documento eliminado"); loadDocs();
    api.get("/documents/expiring").then(({ data }) => setExpiring(data)).catch(() => {});
  };

  useEffect(() => {
    if (visibleSections.length && !visibleSections.find((s) => s.key === tab) && SECTIONS.find((s) => s.key === tab)) {
      setTab(visibleSections[0].key);
    }
  }, [visibleSections, tab]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Gestión Documental</h1>
          <p className="text-slate-500 text-sm mt-1">Contratos, nóminas, formación, PRL/EPIs y salud de tus empleados.</p>
        </div>
        {features?.trial_active && (
          <Badge className="bg-sky-100 text-sky-700 border-0 gap-1.5 py-1.5 px-3" data-testid="trial-badge">
            <Sparkles className="h-3.5 w-3.5" /> Prueba completa · {features.trial_days_left} días restantes
          </Badge>
        )}
      </div>

      {expiring.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50/60 shadow-none" data-testid="expiring-panel">
          <div className="flex items-center gap-2 font-semibold text-amber-800 mb-2"><AlertTriangle className="h-4 w-4" /> Próximos a caducar ({expiring.length})</div>
          <div className="flex flex-wrap gap-2">
            {expiring.slice(0, 8).map((d) => (
              <Badge key={d.id} variant="outline" className={`text-xs ${d.expired ? "border-rose-300 text-rose-600" : "border-amber-300 text-amber-700"}`}>
                {d.employee_name}: {d.title} · {d.expiry_date}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Select value={empFilter || "all"} onValueChange={(v) => setEmpFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64" data-testid="doc-emp-filter"><SelectValue placeholder="Todos los empleados" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los empleados</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          {visibleSections.map((s) => <TabsTrigger key={s.key} value={s.key} data-testid={`tab-${s.key}`}><s.icon className="h-4 w-4 mr-1.5" /> {s.label}</TabsTrigger>)}
          <TabsTrigger value="requests" data-testid="tab-doc-requests"><Inbox className="h-4 w-4 mr-1.5" /> Solicitudes</TabsTrigger>
          {isAdmin && features?.sections?.gestoria && <TabsTrigger value="gestoria" data-testid="tab-gestoria"><Users className="h-4 w-4 mr-1.5" /> Gestoría</TabsTrigger>}
        </TabsList>

        {visibleSections.map((s) => (
          <TabsContent key={s.key} value={s.key} className="mt-6 space-y-4">
            {s.key === "salud" && (
              <Card className="p-4 border-violet-200 bg-violet-50/50 shadow-none flex items-center gap-2 text-sm text-violet-700" data-testid="salud-notice">
                <Lock className="h-4 w-4 shrink-0" /> Contenido protegido por RGPD: puedes subir documentos de salud pero <b className="mx-1">solo el empleado</b> puede verlos con su PIN.
              </Card>
            )}
            {canWrite(s.key) && (
              <Dialog open={uploadOpen && tab === s.key} onOpenChange={(v) => setUploadOpen(v)}>
                <DialogTrigger asChild><Button className="brand-btn" data-testid={`upload-${s.key}-btn`}><Plus className="h-4 w-4 mr-2" /> Subir documento</Button></DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-heading">Subir documento · {s.label}</DialogTitle></DialogHeader>
                  <DocumentUploadForm section={s.key} employees={employees} presetEmployee={empFilter} onSaved={() => { setUploadOpen(false); loadDocs(); api.get("/documents/expiring").then(({ data }) => setExpiring(data)); }} />
                </DialogContent>
              </Dialog>
            )}
            {loading ? <div className="h-32 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
              : <DocTable docs={docs} onDelete={canWrite(s.key) ? delDoc : null} />}
          </TabsContent>
        ))}

        <TabsContent value="requests" className="mt-6"><RequestsTab employees={employees} sections={visibleSections} canWrite={canWrite} /></TabsContent>
        {isAdmin && features?.sections?.gestoria && <TabsContent value="gestoria" className="mt-6"><GestoriaTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function RequestsTab({ employees, sections, canWrite }) {
  const [reqs, setReqs] = useState([]);
  const [form, setForm] = useState({ user_id: "", section: "rrhh", title: "" });
  const load = useCallback(() => api.get("/documents/requests").then(({ data }) => setReqs(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.user_id || !form.title.trim()) return toast.error("Completa empleado y título");
    try { await api.post("/documents/requests", form); toast.success("Solicitud enviada al empleado"); setForm({ user_id: "", section: "rrhh", title: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-5 border-slate-200 shadow-none lg:col-span-1 h-fit">
        <h3 className="font-heading font-semibold mb-3">Solicitar documento</h3>
        <div className="space-y-3">
          <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}><SelectTrigger data-testid="req-employee"><SelectValue placeholder="Empleado" /></SelectTrigger><SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select>
          <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}><SelectTrigger data-testid="req-section"><SelectValue /></SelectTrigger><SelectContent>{sections.filter((s) => canWrite(s.key)).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent></Select>
          <Input placeholder="Qué documento pides (ej. DNI, IBAN...)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="req-title" />
          <Button onClick={create} className="brand-btn w-full" data-testid="req-submit"><Plus className="h-4 w-4 mr-1" /> Enviar solicitud</Button>
        </div>
      </Card>
      <Card className="p-5 border-slate-200 shadow-none lg:col-span-2">
        <h3 className="font-heading font-semibold mb-3">Solicitudes</h3>
        <div className="space-y-2">
          {reqs.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm" data-testid={`req-${r.id}`}>
              <div><span className="font-medium text-slate-700">{r.employee_name}</span> · {r.title}</div>
              <Badge className={r.status === "uploaded" ? "bg-emerald-100 text-emerald-700 border-0" : "bg-amber-100 text-amber-700 border-0"}>{r.status === "uploaded" ? "Subido" : "Pendiente"}</Badge>
            </div>
          ))}
          {!reqs.length && <div className="text-center text-slate-400 py-8 text-sm">No hay solicitudes.</div>}
        </div>
      </Card>
    </div>
  );
}

const PERM_SECTIONS = [["rrhh", "RRHH"], ["formacion", "Formación"], ["prl_epis", "PRL y EPIs"], ["salud", "Salud"]];

function GestoriaTab() {
  const [gestores, setGestores] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankGestor());
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/gestores").then(({ data }) => setGestores(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  function blankGestor() {
    return { name: "", email: "", password: "", permissions: { rrhh: { read: true, write: true }, formacion: { read: true, write: true }, prl_epis: { read: true, write: true }, salud: { read: false, write: true } } };
  }
  const openNew = () => { setEditing(null); setForm(blankGestor()); setOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ name: g.name, email: g.email, password: "", permissions: { ...blankGestor().permissions, ...g.permissions } }); setOpen(true); };
  const setPerm = (sec, key, val) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [sec]: { ...f.permissions[sec], [key]: val } } }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || (!editing && !form.password)) return toast.error("Completa nombre, email y contraseña");
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email, permissions: form.permissions };
      if (form.password) payload.password = form.password;
      if (editing) await api.patch(`/gestores/${editing.id}`, payload);
      else await api.post("/gestores", payload);
      toast.success(editing ? "Gestor actualizado" : "Gestor creado"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("¿Eliminar este gestor?")) return; await api.delete(`/gestores/${id}`); toast.success("Gestor eliminado"); load(); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Usuarios de gestoría/asesoría. <b>No cuentan</b> como empleados ni en facturación.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="brand-btn" data-testid="add-gestor-btn" onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Nuevo gestor</Button></DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar gestor" : "Nuevo gestor"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="gestor-name" /></div>
                <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="gestor-email" /></div>
              </div>
              <div><Label>{editing ? "Nueva contraseña (opcional)" : "Contraseña *"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="gestor-password" /></div>
              <div>
                <Label className="mb-2 block">Permisos por sección</Label>
                <div className="space-y-2">
                  {PERM_SECTIONS.map(([sec, label]) => (
                    <div key={sec} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                      <span className="text-sm font-medium text-slate-700">{label}</span>
                      <div className="flex items-center gap-4">
                        {sec !== "salud" && (
                          <label className="flex items-center gap-1.5 text-xs text-slate-500">Ver <Switch checked={!!form.permissions[sec]?.read} onCheckedChange={(v) => setPerm(sec, "read", v)} data-testid={`perm-${sec}-read`} /></label>
                        )}
                        {sec === "salud" && <span className="text-xs text-slate-400 italic">solo subir</span>}
                        <label className="flex items-center gap-1.5 text-xs text-slate-500">Subir <Switch checked={!!form.permissions[sec]?.write} onCheckedChange={(v) => setPerm(sec, "write", v)} data-testid={`perm-${sec}-write`} /></label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={save} disabled={saving} className="brand-btn w-full" data-testid="save-gestor-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {gestores.map((g) => (
          <Card key={g.id} className="p-4 border-slate-200 shadow-none" data-testid={`gestor-${g.id}`}>
            <div className="flex items-start justify-between">
              <div><div className="font-semibold text-slate-800">{g.name}</div><div className="text-xs text-slate-400">{g.email}</div></div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => openEdit(g)} data-testid={`edit-gestor-${g.id}`}>Permisos</Button>
                <Button size="icon" variant="ghost" className="hover:text-red-500" onClick={() => del(g.id)} data-testid={`del-gestor-${g.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-3">
              {PERM_SECTIONS.map(([sec, label]) => {
                const p = g.permissions?.[sec] || {};
                if (!p.read && !p.write) return null;
                return <Badge key={sec} variant="secondary" className="text-[10px]">{label}: {p.read ? "ver" : ""}{p.read && p.write ? "+" : ""}{p.write ? "subir" : ""}</Badge>;
              })}
            </div>
          </Card>
        ))}
        {!gestores.length && <Card className="p-8 text-center text-slate-400 border-slate-200 shadow-none sm:col-span-2">Aún no hay gestores. Crea uno para que tu asesoría suba la documentación.</Card>}
      </div>
    </div>
  );
}
